/**
 * DemandWeekRow Component
 *
 * Displays a week's worth of demand with a header and strain cards.
 *
 * @module DemandWeekRow
 */

import { Calendar } from 'lucide-react'
import DemandCard from './DemandCard'

/**
 * Format kg value for week total
 *
 * @param {number} kg - Weight in kg
 * @returns {string} Formatted string
 */
function formatWeekTotal(kg) {
  if (kg >= 100) {
    return `${Math.round(kg)}kg total`
  }
  return `${kg.toFixed(1)}kg total`
}

/**
 * Check if week contains the current date
 *
 * @param {Date} weekStart - Start of week
 * @returns {boolean}
 */
function isCurrentWeek(weekStart) {
  const now = new Date()
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  return now >= weekStart && now <= weekEnd
}

/**
 * DemandWeekRow - A week's demand with header and cards
 *
 * @param {Object} props
 * @param {string} props.weekLabel - Human-readable week label
 * @param {Date} props.weekStartDate - Date object for week start
 * @param {Array} props.items - Demand items for this week
 * @param {number} props.totalKg - Total demand for the week
 */
export default function DemandWeekRow({
  weekLabel,
  weekStartDate,
  items,
  totalKg
}) {
  const isCurrent = isCurrentWeek(weekStartDate)

  return (
    <div className={`rounded-lg ${isCurrent ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}>
      {/* Week Header */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-t-lg ${
        isCurrent ? 'bg-blue-50' : 'bg-gray-50'
      }`}>
        <div className="flex items-center gap-2">
          <Calendar className={`w-4 h-4 ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`} />
          <span className={`font-medium ${isCurrent ? 'text-blue-900' : 'text-gray-900'}`}>
            {weekLabel}
          </span>
          {isCurrent && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              This week
            </span>
          )}
        </div>
        <span className="text-sm text-gray-600">{formatWeekTotal(totalKg)}</span>
      </div>

      {/* Demand Cards Grid */}
      <div className="p-4 bg-white rounded-b-lg border border-t-0 border-gray-200">
        {items.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No demand forecasted</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items
              .sort((a, b) => b.totalKg - a.totalKg) // Sort by total kg descending
              .map((item) => (
                <DemandCard
                  key={item.strainName}
                  strainName={item.strainName}
                  recurringKg={item.recurringKg}
                  predictedKg={item.predictedKg}
                  predictedConfidence={item.predictedConfidence}
                  totalKg={item.totalKg}
                  supply={item.supply}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  )
}
